/**
 * The one-attempt lane's shared contract (spec 2026-10-22, decisions 1, 2, 5
 * and 9): a ticket is one durable attempt, the attempt is a Temporal workflow
 * that starts one cloud `/go` session per execution, and the session reports
 * what it did in `result.json`.
 *
 * The record lives in `attempts`, `attempt_executions` and `attempt_events`
 * (migration 0372). `attempts.status` is a cache of the last event that set
 * one, written in the same transaction (`attempts/repo.ts`).
 *
 * `result.json` is the agent's report, never proof: readiness is GitHub's head
 * and CI. The pi extension writes it with its own types (it does not depend on
 * this package), so this parser is the host's half of a contract kept in
 * prose: unknown fields pass, a missing identity field is refused.
 */
import { buildStopCauseClass } from "./buildStopCause.js";
/**
 * Which lane a Team's new work starts on (`desks.lane`), and which lane
 * started a ticket (`tickets.lane`). `session` (2026-10-28) is a way the
 * one-attempt lane runs: one pi session in one Machine for the whole run
 * ({@link ATTEMPT_LANES}). Its tickets are attempts like any other, so
 * `tickets.lane` says `attempt` for them and only `attempts.lane` says
 * `session`; `tickets.lane` never holds `session`.
 */
export const TEAM_LANES = ["legacy", "attempt", "session"];
export function isTeamLane(value) {
    return typeof value === "string" && TEAM_LANES.includes(value);
}
/**
 * How one attempt runs (`attempts.lane`, written once at its start).
 * `attempt` is the stage graph: a fresh sandbox and a fresh `/go --cloud`
 * session per execution. `session` is one Opus pi session in one Machine kept
 * for the whole run, resumed at each checkpoint (the plan's gate, a review, a
 * red CI) with one message.
 */
export const ATTEMPT_LANES = ["attempt", "session"];
export function isAttemptLane(value) {
    return typeof value === "string" && ATTEMPT_LANES.includes(value);
}
/** Whether a Team on this lane starts its new work as an attempt (either way it runs). */
export function laneStartsAttempts(lane) {
    return lane !== "legacy";
}
/**
 * The per-workspace feature flag behind God Mode's "Build lane for every
 * Team" switch (Jack, 2026-10-23). On, every Team in the workspace starts new
 * work on the `attempt` lane whatever its own `desks.lane` says, except a
 * Team whose row says `session`, which keeps it; off, each Team's row
 * decides. A workspace whose `workspaces.build_lane` says `session` puts
 * every Team on the session lane whatever the flag says. The backend's
 * `attempts/lane.ts` is the one reader of all three.
 */
export const BUILD_ONE_ATTEMPT_LANE_FLAG = "build.one_attempt_lane";
export const ATTEMPT_STATUSES = [
    "preparing",
    "planning",
    "awaiting_plan",
    "building",
    "waiting_review",
    "fixing",
    "done",
    "stopped",
];
export function isAttemptStatus(value) {
    return typeof value === "string" && ATTEMPT_STATUSES.includes(value);
}
/** Every status but the two an attempt ends in. */
export function isLiveAttemptStatus(status) {
    return status !== "done" && status !== "stopped";
}
/** What one session (an execution) was started to do. */
export const ATTEMPT_STAGES = ["plan", "implement", "fix", "verify"];
export function isAttemptStage(value) {
    return typeof value === "string" && ATTEMPT_STAGES.includes(value);
}
/**
 * The append-only event kinds. `merged` is the attempt's own end in success:
 * the spec's list names every step and failure but no event for done, and the
 * status is a cache of the last event, so done needs one. `ci_failed` is CI
 * red on a clean head and what the attempt does about it: a fix, or a note
 * saying why not (YAG-1052). `base_measured` is what a session measured on
 * the attempt's base (`checkAttribution.ts`), kept so the next session
 * measures only what is missing. `transcript_stored` is one part of a
 * session-lane attempt's pi transcript kept in the workspace's bucket, with
 * the byte range it covers (YAG-1094). `note_read` is the session that took
 * up a person's note for the next step (YAG-1116).
 */
export const ATTEMPT_EVENT_KINDS = [
    "prepared",
    "session_started",
    "plan_ready",
    "plan_answered",
    "pushed",
    "checks_recorded",
    "pr_opened",
    "pr_ready",
    "review_received",
    "resumed",
    "finished",
    "stopped",
    "handed_off",
    "session_lost",
    "collect_failed",
    "push_failed",
    "result_superseded",
    "github_call",
    "merged",
    "ci_failed",
    "base_measured",
    "transcript_stored",
    "note_read",
];
export function isAttemptEventKind(value) {
    return typeof value === "string" && ATTEMPT_EVENT_KINDS.includes(value);
}
/**
 * The lane's hand-off ask (`handOffActivity`), named by what answering it
 * does: `run_setup` when the repository has no prepared environment, so its
 * setup runs first; `resume` for every other stop. The gate's payload carries
 * it as `askKind`, and the projection puts it on the row as
 * `currentGateAskKind`, so the queue row and the sheet read one rule.
 */
export const HAND_OFF_ASK_KINDS = ["run_setup", "resume"];
export function isHandOffAskKind(value) {
    return typeof value === "string" && HAND_OFF_ASK_KINDS.includes(value);
}
/**
 * The ask a stop with this cause hands off with. Run setup is offered only
 * where a setup can help: not for a repository whose environment builds
 * other repositories as dependencies (`missing: "dependencies"` on the
 * `prepared` event), not for workspace variables it needs set
 * (`missing: "variables"`), and not for an attempt that names no repository.
 */
export function handOffAskKindOf(cause, facts = {}) {
    const setupHelps = cause === "env_needs_you" &&
        facts.missing !== "dependencies" &&
        facts.missing !== "variables" &&
        facts.hasRepository !== false;
    return setupHelps ? "run_setup" : "resume";
}
/**
 * The ask kind a hand-off's payload carries (its gate's, or its `handed_off`
 * event's), or, for one written before the kind was kept, the one its cause
 * gives. The row and the sheet read the same gate through this one rule.
 */
export function handOffAskKindIn(payload) {
    if (isHandOffAskKind(payload.askKind))
        return payload.askKind;
    const cause = payload.cause ?? payload.stopReason;
    return handOffAskKindOf(typeof cause === "string" ? cause : "", { missing: payload.missing });
}
/**
 * A stop on our side that the lane handed off: what the queue row says and
 * what the sheet's ask asks, word for word.
 */
export const INFRA_HAND_OFF_SENTENCE = "Stopped on our side; YAGNI retried and then opened an ask";
/**
 * Where the lane's class for a stop is not the cause's own. An install that
 * failed is a person's: on the lane it comes before any agent spends, the
 * repository's setup (or a variable it needs) failed on this commit, and
 * running again as it is fails the same way. A session's own limits (its
 * spend cap and clock, `run_budget`; its review rounds, `round_cap`) are
 * backstops against a session gone wrong, so ours: the ticket's one budget
 * is what a person sets (decision 6). A sandbox whose disk filled again is
 * ours too: the attempt notes it and a person runs it again.
 */
const LANE_STOP_CLASS = {
    env_install_failed: "person",
    env_disk_full: "infra",
    run_budget: "infra",
    round_cap: "infra",
};
/**
 * The class the lane gives a stop: the cause's own (`buildStopCauseClass`)
 * but for {@link LANE_STOP_CLASS}. A person's opens the lane's one ask; ours
 * opens none, and the attempt stops with a notice and Run again
 * (decision 5, 2026-10-19). The legacy build keeps the cause's own class.
 */
export function handOffStopClassOf(cause) {
    return LANE_STOP_CLASS[cause] ?? buildStopCauseClass(cause);
}
/** The hand-off ask off a gate's payload (its stop names the attempt), or null for any other gate. */
export function readHandOffAsk(payload) {
    if (typeof payload?.attemptId !== "string" || typeof payload.stopReason !== "string")
        return null;
    const setup = payload.setup;
    return {
        askKind: handOffAskKindIn(payload),
        repoId: typeof payload.repoId === "string" && payload.repoId ? payload.repoId : null,
        stoppedBeforeWork: payload.stoppedBeforeWork === true,
        setupState: typeof setup?.state === "string" ? setup.state : null,
    };
}
/** How a session ended, as `result.json` says (the pipeline's `StopReason`). */
export const ATTEMPT_STOP_REASONS = [
    "clean",
    "round_cap",
    "no_changes",
    "budget_exceeded",
    "failed",
    "aborted",
];
export const CHECK_BASELINES = ["runnable", "fails_on_base", "cannot_run"];
/**
 * How the session's note starts on a plan's check its last gate run did not
 * run on the tree it reports (the build's check budget was spent, the check
 * was not reached, the build stopped first): no exit code, and how it went
 * on an earlier tree after these words. The pi extension writes them
 * (`CHECK_NOT_RUN_NOTE` in pipeline/verify.ts); a change here is a change there.
 */
export const CHECK_NOT_RUN_NOTE = "not run on this tree";
/** A plan's check the session listed without running it on the head it reports. */
export function checkNotRunOnHead(check) {
    return check.exitCode === null && (check.note?.startsWith(CHECK_NOT_RUN_NOTE) ?? false);
}
/**
 * A check that ran and passed, or failed only the way it already failed on
 * the base. The session labels a failed check `fails_on_base` only when the
 * base had every one of its failures (`attributeFailure`); a check with a
 * failure of its own, or with no run on the base to compare with, is red.
 */
export function checkIsGreen(check) {
    return check.exitCode !== null && (check.exitCode === 0 || check.baseline === "fails_on_base");
}
/** How many open findings a session's record keeps, and how much of each. */
export const STOP_OPEN_FINDINGS_MAX = 5;
const STOP_OPEN_FINDING_CHARS = 200;
/**
 * The findings a spent round cap's stop names: its blocking ones (an advisory
 * one never held the change), each on one line and bounded, at most
 * {@link STOP_OPEN_FINDINGS_MAX}. `count` is how many blocked in all.
 */
export function stopOpenFindings(findings, 
/** Clears credentials from a whole message before it is bounded (the backend's `scrubSecrets`). */
scrub = (text) => text) {
    // A message that is only punctuation or a quoted output says nothing to name.
    const named = findings
        .filter((finding) => finding.severity === "blocking")
        .map((finding) => ({
        file: finding.file,
        line: finding.line ?? null,
        message: openFindingLine(scrub(finding.message)),
    }))
        .filter((finding) => finding.message !== "");
    return { findings: named.slice(0, STOP_OPEN_FINDINGS_MAX), count: named.length };
}
/**
 * The findings a finished session's review left open, as its `finished`
 * event keeps them for the pull request, the row and the sheet to name
 * (YAG-1106): every severity, the blocking ones first, each on one line and
 * bounded, at most {@link STOP_OPEN_FINDINGS_MAX}. `count` is how many were
 * open in all. A round cap no longer ends a build without its pull request,
 * so what the cap left open is said beside the pull request rather than as
 * a stop.
 */
export function finishedOpenFindings(findings, 
/** Clears credentials from a whole message before it is bounded (the backend's `scrubSecrets`). */
scrub = (text) => text) {
    const named = [...findings]
        .sort((a, b) => Number(b.severity === "blocking") - Number(a.severity === "blocking"))
        .map((finding) => ({
        file: finding.file,
        line: finding.line ?? null,
        message: openFindingLine(scrub(finding.message)),
        severity: finding.severity,
    }))
        .filter((finding) => finding.message !== "");
    return { findings: named.slice(0, STOP_OPEN_FINDINGS_MAX), count: named.length };
}
/** A finding on one line, without a quoted command's output, bounded. */
export function openFindingLine(message) {
    const head = (message.split(/\bOutput tail\b/)[0] ?? "")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/[.;:\s]+$/, "");
    return head.length > STOP_OPEN_FINDING_CHARS
        ? `${head.slice(0, STOP_OPEN_FINDING_CHARS - 1).trimEnd()}…`
        : head;
}
/** Where a finding points: "src/a.ts:12", "src/a.ts", or null when it names no file. */
export function stopOpenFindingPlace(finding) {
    if (!finding.file)
        return null;
    return finding.line !== null ? `${finding.file}:${finding.line}` : finding.file;
}
/**
 * The open findings a `stopped` or a `finished` event's payload kept;
 * anything malformed is left out, and a severity it did not keep is absent.
 */
export function readStopOpenFindings(payload) {
    const raw = payload.openFindings;
    if (!Array.isArray(raw))
        return [];
    return raw.flatMap((entry) => {
        const value = record(entry);
        const message = value ? text(value.message) : null;
        if (!value || !message)
            return [];
        const line = value.line;
        const severity = value.severity;
        return [
            {
                file: typeof value.file === "string" ? value.file : "",
                line: typeof line === "number" && Number.isInteger(line) && line > 0 ? line : null,
                message,
                ...(severity === "blocking" || severity === "advisory" ? { severity } : {}),
            },
        ];
    });
}
/** The most plan changes a report carries, and the longest each one's words. */
export const MAX_PLAN_CHANGES = 20;
export const MAX_PLAN_CHANGE_CHARS = 600;
/** A change's words on one line, clipped; null for none. */
function planChangeWords(value) {
    if (typeof value !== "string")
        return null;
    const words = value.replace(/\s+/g, " ").trim();
    return words ? words.slice(0, MAX_PLAN_CHANGE_CHARS) : null;
}
/** The report's plan changes: each with what was done instead, the rest dropped. */
export function parsePlanChanges(value) {
    if (!Array.isArray(value))
        return [];
    const changes = [];
    for (const entry of value) {
        if (changes.length >= MAX_PLAN_CHANGES)
            break;
        if (!entry || typeof entry !== "object" || Array.isArray(entry))
            continue;
        const raw = entry;
        const instead = planChangeWords(raw.instead);
        const step = raw.step;
        const numbered = typeof step === "number" && Number.isInteger(step) && step > 0;
        if (!instead || !(numbered || step === null || step === undefined))
            continue;
        changes.push({ step: numbered ? step : null, instead, why: planChangeWords(raw.why) ?? "" });
    }
    return changes;
}
const GIT_SHA = /^[0-9a-f]{40}([0-9a-f]{24})?$/;
function record(value) {
    return value && typeof value === "object" && !Array.isArray(value)
        ? value
        : null;
}
function text(value) {
    return typeof value === "string" && value.length > 0 ? value : null;
}
function count(value) {
    return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : 0;
}
function strings(value) {
    return Array.isArray(value)
        ? value.filter((entry) => typeof entry === "string")
        : [];
}
function check(value) {
    const raw = record(value);
    if (!raw)
        return null;
    const name = text(raw.name);
    const command = text(raw.command);
    const baseline = raw.baseline;
    const exitCode = raw.exitCode;
    if (!name || !command || (typeof exitCode !== "number" && exitCode !== null))
        return null;
    if (!CHECK_BASELINES.includes(baseline))
        return null;
    return {
        name,
        command,
        cwd: typeof raw.cwd === "string" ? raw.cwd : ".",
        exitCode,
        durationMs: count(raw.durationMs),
        baseline: baseline,
        note: text(raw.note),
    };
}
function finding(value) {
    const raw = record(value);
    if (!raw)
        return null;
    // The session leaves `file` out of a finding that names none (a missing
    // test, a whole-change concern): it is still open, with no place.
    const file = typeof raw.file === "string" ? raw.file : "";
    const message = text(raw.message);
    if (!message)
        return null;
    const line = raw.line;
    return {
        file,
        ...(typeof line === "number" && Number.isInteger(line) && line > 0 ? { line } : {}),
        message,
        severity: raw.severity === "blocking" ? "blocking" : "advisory",
    };
}
function usage(value) {
    const raw = record(value);
    if (!raw)
        return { tokens: 0, costMillicents: 0, byStage: [] };
    const byStage = Array.isArray(raw.byStage)
        ? raw.byStage.flatMap((entry) => {
            const stage = record(entry);
            const name = stage ? text(stage.stage) : null;
            return stage && name
                ? [
                    {
                        stage: name,
                        tokens: count(stage.tokens),
                        costMillicents: count(stage.costMillicents),
                    },
                ]
                : [];
        })
        : [];
    return { tokens: count(raw.tokens), costMillicents: count(raw.costMillicents), byStage };
}
/**
 * Read a session's `result.json` (the parsed object or its text). Refuses a
 * report missing `version`, `attemptId`, `executionId`, `generation`, `stage`,
 * `headSha` or `stopReason`; keeps fields it does not know out of the value
 * without failing on them; drops a malformed check or finding rather than
 * trusting it.
 */
export function parseAttemptResult(input) {
    let raw = input;
    if (typeof input === "string") {
        try {
            raw = JSON.parse(input);
        }
        catch {
            return { ok: false, error: "result.json is not JSON" };
        }
    }
    const body = record(raw);
    if (!body)
        return { ok: false, error: "result.json is not an object" };
    if (body.version !== 1)
        return { ok: false, error: "result.json version must be 1" };
    const attemptId = text(body.attemptId);
    if (!attemptId)
        return { ok: false, error: "result.json has no attemptId" };
    const executionId = text(body.executionId);
    if (!executionId)
        return { ok: false, error: "result.json has no executionId" };
    const generation = body.generation;
    if (typeof generation !== "number" || !Number.isSafeInteger(generation) || generation < 1) {
        return { ok: false, error: "result.json has no generation" };
    }
    if (!isAttemptStage(body.stage))
        return { ok: false, error: "result.json has no stage" };
    const headSha = text(body.headSha);
    if (!headSha || !GIT_SHA.test(headSha))
        return { ok: false, error: "result.json has no headSha" };
    if (!ATTEMPT_STOP_REASONS.includes(body.stopReason)) {
        return { ok: false, error: "result.json has no stopReason" };
    }
    const baseSha = text(body.baseSha);
    return {
        ok: true,
        value: {
            version: 1,
            attemptId,
            executionId,
            generation,
            stage: body.stage,
            headSha,
            branch: text(body.branch),
            baseSha: baseSha && GIT_SHA.test(baseSha) ? baseSha : null,
            stopReason: body.stopReason,
            rounds: count(body.rounds),
            checks: Array.isArray(body.checks) ? body.checks.flatMap((entry) => check(entry) ?? []) : [],
            findingsOpen: Array.isArray(body.findingsOpen)
                ? body.findingsOpen.flatMap((entry) => finding(entry) ?? [])
                : [],
            filesChanged: strings(body.filesChanged),
            commits: strings(body.commits).filter((sha) => GIT_SHA.test(sha)),
            notes: strings(body.notes),
            blockingKey: text(body.blockingKey),
            usage: usage(body.usage),
            planChanges: parsePlanChanges(body.planChanges),
        },
    };
}
//# sourceMappingURL=attempts.js.map